import { useState, useEffect, type FormEvent } from 'react'
import { z } from 'zod'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Save, Calendar, RepeatIcon } from 'lucide-react'
import { format } from 'date-fns'
import { usePickup, useCreatePickup, useUpdatePickup } from '@/hooks/queries/usePickups'
import { useClients } from '@/hooks/queries/useClients'
import { usePickupOptions } from '@/hooks/queries/usePickupOptions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Combobox } from '@/components/ui/combobox'
import type { ClientBranch, PickupFormData, PickupStatus, RecurrencePattern } from '@/types'


const pickupSchema = z.object({
  client_id: z.number().refine((v) => v > 0, 'Debes seleccionar un cliente'),
  client_branch_id: z.number().refine((v) => v > 0, 'Debes seleccionar una sucursal'),
  scheduled_date: z.string().min(1, 'La fecha programada es obligatoria'),
  status: z.enum(['scheduled', 'confirmed', 'completed', 'rescheduled', 'cancelled']),
  is_recurring: z.boolean(),
  recurrence_pattern: z.string().nullable(),
  notes: z.string().optional(),
}).refine(
  (d) => !d.is_recurring || Boolean(d.recurrence_pattern),
  { message: 'Debes seleccionar la frecuencia de recurrencia', path: ['recurrence_pattern'] }
)

const INITIAL_FORM: PickupFormData = {
  client_id: 0,
  client_branch_id: 0,
  scheduled_date: format(new Date(), 'yyyy-MM-dd'),
  status: 'scheduled',
  is_recurring: false,
  recurrence_pattern: null,
  notes: '',
}

export function PickupFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(id)
  const pickupId = id ? parseInt(id, 10) : 0

  const [form, setForm] = useState<PickupFormData>(() => ({
    ...INITIAL_FORM,
    scheduled_date: searchParams.get('date') ?? INITIAL_FORM.scheduled_date,
  }))
  const [errors, setErrors] = useState<Partial<Record<keyof PickupFormData, string>>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: existingPickup, isLoading: isLoadingPickup } = usePickup(pickupId)
  const { data: clientsData } = useClients()
  const { data: pickupOptions } = usePickupOptions()
  const createMutation = useCreatePickup()
  const updateMutation = useUpdatePickup()

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  useEffect(() => {
    if (isEdit && existingPickup) {
      setForm({
        client_id: existingPickup.client_id,
        client_branch_id: existingPickup.client_branch_id ?? 0,
        scheduled_date: existingPickup.scheduled_date,
        status: existingPickup.status,
        is_recurring: existingPickup.is_recurring,
        recurrence_pattern: existingPickup.recurrence_pattern,
        notes: existingPickup.notes ?? '',
      })
    }
  }, [isEdit, existingPickup])

  const selectedClient = clientsData?.find((client) => client.id === form.client_id)
  const availableBranches = (selectedClient?.branches ?? []).filter(
    (branch): branch is ClientBranch & { id: number } => typeof branch.id === 'number'
  )

  useEffect(() => {
    if (!form.client_id) return
    if (availableBranches.length === 0) return

    const hasCurrentBranch = availableBranches.some((branch) => branch.id === form.client_branch_id)
    if (hasCurrentBranch) return

    const autoSelectedBranch = availableBranches.length === 1
      ? availableBranches[0]
      : availableBranches.find((branch) => branch.is_primary)

    if (!autoSelectedBranch) return

    setForm((prev) => {
      if (prev.client_id !== form.client_id || prev.client_branch_id === autoSelectedBranch.id) {
        return prev
      }

      return {
        ...prev,
        client_branch_id: autoSelectedBranch.id,
      }
    })
  }, [form.client_id, form.client_branch_id, availableBranches])

  const handleClientChange = (value: string) => {
    const nextClientId = parseInt(value, 10)
    const nextClient = clientsData?.find((client) => client.id === nextClientId)
    const nextBranches = (nextClient?.branches ?? []).filter(
      (branch): branch is ClientBranch & { id: number } => typeof branch.id === 'number'
    )
    const autoSelectedBranch = nextBranches.length === 1
      ? nextBranches[0]
      : nextBranches.find((branch) => branch.is_primary)

    setForm((prev) => ({
      ...prev,
      client_id: nextClientId,
      client_branch_id: autoSelectedBranch?.id ?? 0,
    }))
    setErrors((prev) => ({
      ...prev,
      client_id: undefined,
      client_branch_id: undefined,
    }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    const result = pickupSchema.safeParse(form)
    if (!result.success) {
      const mapped: Partial<Record<keyof PickupFormData, string>> = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof PickupFormData
        if (!mapped[field]) mapped[field] = issue.message
      }
      setErrors(mapped)
      return
    }
    setErrors({})

    try {
      const payload = {
        ...form,
        recurrence_pattern: form.is_recurring ? form.recurrence_pattern : null,
      }

      if (isEdit) {
        await updateMutation.mutateAsync({ id: pickupId, data: payload })
      } else {
        await createMutation.mutateAsync(payload)
      }
      navigate('/pickups')
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as {
          response?: { data?: { message?: string; errors?: Record<string, string[]> } }
        }
        const serverErrors = axiosErr.response?.data?.errors
        if (serverErrors) {
          const mapped: Partial<Record<keyof PickupFormData, string>> = {}
          for (const [key, msgs] of Object.entries(serverErrors)) {
            mapped[key as keyof PickupFormData] = msgs[0]
          }
          setErrors(mapped)
        } else {
          setSubmitError(
            axiosErr.response?.data?.message ?? 'Error al guardar el retiro'
          )
        }
      } else {
        setSubmitError('Error inesperado. Por favor intente nuevamente.')
      }
    }
  }

  if (isEdit && isLoadingPickup) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Encabezado */}
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/pickups')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Editar retiro' : 'Programar retiro'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? `Editando retiro #${pickupId}`
              : 'Programa un nuevo retiro de residuos'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Detalles del retiro</CardTitle>
          <CardDescription>
            Configura el cliente, fecha y periodicidad del retiro
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {submitError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="client_id">
                  Cliente <span className="text-red-500">*</span>
                </Label>
                <Combobox
                  options={clientsData?.map((c) => ({ value: String(c.id), label: c.trade_name })) ?? []}
                  value={form.client_id ? String(form.client_id) : null}
                  onChange={handleClientChange}
                  placeholder="Selecciona un cliente..."
                />
                {errors.client_id && (
                  <p className="text-xs text-red-600">{errors.client_id}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="client_branch_id">
                  Sucursal <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={form.client_branch_id ? String(form.client_branch_id) : ''}
                  onValueChange={(v) => {
                    setForm((prev) => ({ ...prev, client_branch_id: parseInt(v, 10) }))
                    setErrors((prev) => ({ ...prev, client_branch_id: undefined }))
                  }}
                  disabled={isSubmitting || !form.client_id || availableBranches.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={form.client_id ? 'Selecciona una sucursal...' : 'Selecciona un cliente primero'} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBranches.map((branch) => (
                      <SelectItem key={branch.id} value={String(branch.id)}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.client_branch_id && (
                  <p className="text-xs text-red-600">{errors.client_branch_id}</p>
                )}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="scheduled_date">
                  <Calendar className="mr-1 inline h-3.5 w-3.5" />
                  Fecha programada <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="scheduled_date"
                  type="date"
                  value={form.scheduled_date}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      scheduled_date: e.target.value,
                    }))
                  }
                  disabled={isSubmitting}
                />
                {errors.scheduled_date && (
                  <p className="text-xs text-red-600">{errors.scheduled_date}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Estado</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) =>
                    setForm((prev) => ({ ...prev, status: v as PickupStatus }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(pickupOptions?.statuses ?? []).map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recurrencia */}
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <input
                  id="is_recurring"
                  type="checkbox"
                  checked={form.is_recurring}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_recurring: e.target.checked,
                      recurrence_pattern: e.target.checked ? 'weekly' : null,
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <Label htmlFor="is_recurring" className="cursor-pointer">
                  <RepeatIcon className="inline h-3.5 w-3.5 mr-1" />
                  Retiro recurrente
                </Label>
              </div>

              {form.is_recurring && (
                <div className="space-y-1.5">
                  <Label>Frecuencia</Label>
                  <Select
                    value={form.recurrence_pattern ?? ''}
                    onValueChange={(v) =>
                      setForm((prev) => ({
                        ...prev,
                        recurrence_pattern: v as RecurrencePattern,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona la frecuencia..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(pickupOptions?.recurrence_patterns ?? []).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.recurrence_pattern && (
                    <p className="text-xs text-red-600">
                      {errors.recurrence_pattern}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Notas */}
            <div className="space-y-1.5">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Instrucciones especiales, acceso al lugar, etc."
                disabled={isSubmitting}
                rows={3}
              />
            </div>

            {/* Botones */}
            <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row">
              <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEdit ? 'Actualizar retiro' : 'Programar retiro'}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => navigate('/pickups')}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
